import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, IconButton, Button, Drawer } from '@mui/material';
import IsMobile from '../../../../hooks/isMobile';
import CloseIcon from '../../../../assets/svg-fc/CrossIconSvg';
import OrderIdFilterField from './FilterFields/OrderIdFilterField';
import EventDateFilterField from './FilterFields/EventDateFilterField';
import CreatedDateFilterField from './FilterFields/CreatedDateFilterField';
import EventStatusFilterField from './FilterFields/EventStatusFilterField';
import EventTypeFilterField from './FilterFields/EventTypeFilterField';
import palette from '../../../../theme/palette';
import { ChatFilterState } from '../../../../interfaces/chats';
import './chatsFilter.scss';

interface ChatsFilterProps {
  open: boolean;
  initialFilters: ChatFilterState;
  onClose: () => void;
  onApply: (filters: ChatFilterState) => void;
}

const ChatsFilter: React.FC<ChatsFilterProps> = ({ open, onClose, onApply, initialFilters }) => {
  const isMobile = IsMobile();
  const [filters, setFilters] = useState<ChatFilterState>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<ChatFilterState>(initialFilters);

  // Sync appliedFilters with initialFilters prop when it changes
  useEffect(() => {
    setAppliedFilters(initialFilters);
  }, [initialFilters]);

  // Reset filters to appliedFilters when opening the filter drawer
  useEffect(() => {
    if (open) {
      setFilters(appliedFilters);
    }
  }, [open, appliedFilters]);

  const handleApply = () => {
    setAppliedFilters(filters);
    onApply(filters);
    onClose();
  };

  const handleCancel = () => {
    setFilters(appliedFilters);
    onClose();
  };

  const handleOrderIdChange = (value: string) => {
    setFilters((prev) => ({ ...prev, orderId: value }));
  };

  const handleEventDateChange = (startDate: Date | null, endDate: Date | null) => {
    setFilters((prev) => ({ ...prev, eventDateStart: startDate, eventDateEnd: endDate }));
  };

  const handleCreatedDateChange = (startDate: Date | null, endDate: Date | null) => {
    setFilters((prev) => ({ ...prev, createdDateStart: startDate, createdDateEnd: endDate }));
  };

  const handleEventStatusChange = (values: string[]) => {
    setFilters((prev) => ({ ...prev, vendorEventStatus: values }));
  };

  const handleEventTypeChange = (value: string) => {
    setFilters((prev) => ({ ...prev, eventType: value }));
  };

  // Check if any filters are selected
  const hasFiltersSelected = useMemo(() => {
    return (
      filters.orderId.trim() !== '' ||
      filters.eventDateStart !== null ||
      filters.eventDateEnd !== null ||
      filters.createdDateStart !== null ||
      filters.createdDateEnd !== null ||
      filters.vendorEventStatus.length > 0 ||
      filters.eventType.trim() !== ''
    );
  }, [filters]);

  const filterContent = (
    <Box className="chatsFilterContent">
      <Box className="chatsFilterHeader">
        <Typography className="chatsFilterTitle">Filter chats by</Typography>
        <IconButton className="chatsFilterCloseButton" onClick={onClose} size="small">
          <CloseIcon size="18" color={palette.grey[700]} />
        </IconButton>
      </Box>
      <Box className="chatsFilterBody">
        <OrderIdFilterField value={filters.orderId} onChange={handleOrderIdChange} />
        <EventDateFilterField
          value={{ startDate: filters.eventDateStart, endDate: filters.eventDateEnd }}
          onChange={handleEventDateChange}
        />
        <CreatedDateFilterField
          value={{ startDate: filters.createdDateStart, endDate: filters.createdDateEnd }}
          onChange={handleCreatedDateChange}
        />
        <EventStatusFilterField value={filters.vendorEventStatus} onChange={handleEventStatusChange} />
        <EventTypeFilterField value={filters.eventType} onChange={handleEventTypeChange} />
      </Box>
      <Box className="chatsFilterFooter">
        <Button className="chatsFilterCancelButton" onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          className="chatsFilterApplyButton"
          onClick={handleApply}
          variant="contained"
          disabled={!hasFiltersSelected}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
    slotProps={{
      paper: {
      className: 'chatsFilterDrawerMobile',
      }
        }}
      >
        {filterContent}
      </Drawer>
    );
  }

  return open ? <Box className="chatsFilterOverlay">{filterContent}</Box> : null;
};

export default ChatsFilter;
